import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap, filter, take } from 'rxjs/operators';
import { MenuAccessService } from '../services/menu-access.service';
import { ProfileService } from '../services/profile.service';

interface IAccessList {
  key: string;
  access: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class MenuAccessGuard implements CanActivate {
  constructor(
    private menuAccessService: MenuAccessService,
    private router: Router,
    private profileService: ProfileService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const url = state.url;

    return this.profileService.profileData$.pipe(
      filter((profile) => !!profile),
      take(1),
      switchMap((profile) => {
        if (profile?.login_as_team) {
          return this.menuAccessService.fetchMenuAccess().pipe(
            map((accessList) => {
              const allowed = this.checkAccessByUrl(url, accessList);

              if (!allowed) {
                const firstAllowed = this.findFirstAllowedPath(accessList);
                const redirectPath = firstAllowed || '/dashboard';

                // Break infinite loop if redirectPath is same as current URL
                if (redirectPath === url) {
                  return true;
                }

                return this.router.parseUrl(redirectPath);
              }

              return true;
            }),
            catchError(() => of(this.router.parseUrl('/dashboard')))
          );
        }

        return of(true);
      }),
      catchError(() => of(this.router.parseUrl('/dashboard')))
    );
  }

  private checkAccessByUrl(url: string, accessList: IAccessList[]): boolean {
    const key = this.mapUrlToKey(url);
    if (!key) return false;

    if (key === 'profile') return true;

    const menu = accessList.find((item) => item.key === key);
    return menu?.access ?? false;
  }

  private mapUrlToKey(url: string): string | undefined {
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Handle dynamic/nested routes with startsWith
    if (cleanUrl.startsWith('/order-data')) {
      return 'shipping_order_data';
    }
    if (cleanUrl.startsWith('/finance')) {
      return 'finance';
    }

    const urlToKeyMap: { [key: string]: string } = {
      '/dashboard': 'dashboard',
      '/order-data': 'shipping_order_data',
      '/ticketing': 'shipping_issue',
      '/rates-track': 'shipping_rates_track',
      '/misc/simulator': 'shipping_simulator',
      '/misc/custom-label': 'custom_label',
      '/payment': 'payment_page',
      '/qrisly': 'qrisly',
      '/generate-qris': 'generate_qris',
      '/misc/invoices': 'payment_invoices',
      '/billings': 'billing',
      '/subscriptions': 'subscriptions',
      '/webhook': 'webhook',
      '/settings': 'developer_settings',
      '/account/document': 'document',
      '/docs-plugin': 'api_docs_plugin',
      '/integration': 'api_docs_plugin',
      '/account/profile': 'profile',
      '/account/bank-account': 'bank_account',
      '/account/pin': 'pin',
      '/account/expedition': 'expedition',
      '/account/teams': 'teams',
    };

    return urlToKeyMap[cleanUrl];
  }

  private findFirstAllowedPath(accessList: IAccessList[]): string | null {
    const keyToUrlMap: { [key: string]: string } = {
      dashboard: '/dashboard',
      shipping_order_data: '/order-data',
      shipping_issue: '/ticketing',
      shipping_rates_track: '/rates-track',
      shipping_simulator: '/misc/simulator',
      custom_label: '/misc/custom-label',
      payment_page: '/payment',
      qrisly: '/qrisly',
      generate_qris: '/generate-qris',
      payment_invoices: '/misc/invoices',
      finance: '/finance',
      billing: '/subscriptions',
      subscriptions: '/subscriptions',
      webhook: '/webhook',
      developer_settings: '/settings',
      document: '/account/document',
      api_docs_plugin: '/integration',
      profile: '/account/profile',
      bank_account: '/account/bank-account',
      pin: '/account/pin',
      expedition: '/account/expedition',
      teams: '/account/teams',
    };

    const firstAllowed = accessList.find((item) => item.access && keyToUrlMap[item.key]);
    return firstAllowed ? keyToUrlMap[firstAllowed.key] : null;
  }
}
